import { IVec2 } from '../utils/vec2';
import { IAudioFiles, IAudioFile } from './AudioFiles';
import { DancerTrackType, GlobalTrackType, ITrackId, TrackType } from './Selection';

export interface IAudioClip {
	id: number;
	audioFileId: number; // Id that references an IAudioFile
	time: number;
	duration: number;
	trackId?: ITrackId;
}

export interface IAudioTrack {
	muted: boolean;
	clips: IAudioClip[];
}

export enum DancerPathNodeShape {
	Triangle,
	Heart,
	Square,
	Tear,
}

export enum DancerPathColor {
	Red,
	Green,
	Blue,
}

export const getDancerPathColor = (dancerPathColor: DancerPathColor): string => {
	switch (dancerPathColor) {
		case DancerPathColor.Red: {
			return '#EA1C1C';
		}
		case DancerPathColor.Green: {
			return '#50C927';
		}
		case DancerPathColor.Blue: {
			return '#00A2E8';
		}
	}
};

export interface IDancerPathNode {
	id: number;
	name: string;
	time: number;
	delay: number; // Time spent at this node before moving to the next
	pos: IVec2;
}

export interface IDancerPathNodeId {
	dancerId: number;
	pathNodeId: number;
}

export interface IAudioGapId {
	afterClipId: number; // Audio clip that is after the Gap
}

export interface IDancer {
	id: number;
	name: string;
	height: number;
	pathNodeShape: DancerPathNodeShape;
	pathColor: DancerPathColor;
	pathNodes: IDancerPathNode[];
	nextPathNodeId: number;

	// Tracks
	aboveHeadTrack: IAudioTrack;
	headTrack: IAudioTrack;
	torsoTrack: IAudioTrack;
	legsTrack: IAudioTrack;
	groundTrack: IAudioTrack;
}

export interface ITimeline {
	dancers: IDancer[];
	musicTrack: IAudioTrack;
	ambienceTrack: IAudioTrack;
	foleyTrack: IAudioTrack;
	duration: number;
	nextAudioClipId: number;
	nextDancerId: number;
	selectedAudioClip: IAudioClip | null;
	selectedAudioGap: IAudioGapId | null;
	selectedDancerPathNode: IDancerPathNodeId | null;
	selectedDancerId: number | null;
}

export const dancerDefaultSpeed = 75; // cm/s

export const isTrackIdEqual = (a: ITrackId, b: ITrackId) => {
	switch (a.trackType) {
		case TrackType.Global: {
			if (b.trackType !== TrackType.Global) return false;
			return a.type === b.type;
		}
		case TrackType.Dancer: {
			if (b.trackType !== TrackType.Dancer) return false;
			return a.type === b.type && a.dancerId === b.dancerId;
		}
	}
}

export const fixDancerPathNodesArray = (pathNodes: IDancerPathNode[]): IDancerPathNode[] => {
	// Sort array by times
	let sortedPathNodes = [...pathNodes].sort((a, b) => a.time - b.time);

	// Make sure delay doesn't bleed over into next node
	for (let i = 0; i < sortedPathNodes.length - 1; i++) {
		const curPathNode = sortedPathNodes[i];
		const nextPathNode = sortedPathNodes[i + 1];
		if (curPathNode.delay < 0) {
			sortedPathNodes[i] = {
				...curPathNode,
				delay: 0,
			};
		} else if (curPathNode.time + curPathNode.delay > nextPathNode.time) {
			sortedPathNodes[i] = {
				...curPathNode,
				delay: nextPathNode.time - curPathNode.time,
			};
		}
	}

	return sortedPathNodes;
};

export const findNextAudioClipStartOrEndTime = (timeline: ITimeline, currentTime: number): number | null => {
	
	function findNextInAudioTrack(track: IAudioTrack): number {
		let nextTime = Infinity;
		for (const clip of track.clips) {
			if (clip.time > currentTime) {
				nextTime = Math.min(nextTime, clip.time);
			}
			if (clip.time + clip.duration > currentTime) {
				nextTime = Math.min(nextTime, clip.time + clip.duration);
			}
		}
		
		return nextTime;
	}

	let nextTime = Infinity;
	nextTime = Math.min(nextTime, findNextInAudioTrack(timeline.musicTrack));
	nextTime = Math.min(nextTime, findNextInAudioTrack(timeline.ambienceTrack));
	nextTime = Math.min(nextTime, findNextInAudioTrack(timeline.foleyTrack));

	for (const dancer of timeline.dancers) {
		nextTime = Math.min(nextTime, findNextInAudioTrack(dancer.aboveHeadTrack));
		nextTime = Math.min(nextTime, findNextInAudioTrack(dancer.headTrack));
		nextTime = Math.min(nextTime, findNextInAudioTrack(dancer.torsoTrack));
		nextTime = Math.min(nextTime, findNextInAudioTrack(dancer.legsTrack));
		nextTime = Math.min(nextTime, findNextInAudioTrack(dancer.groundTrack));
	}

	if (nextTime < Infinity) {
		return nextTime;
	}

	return null;
}

export const findPrevAudioClipStartOrEndTime = (timeline: ITimeline, currentTime: number): number | null => {
	
	function findPrevInAudioTrack(track: IAudioTrack): number {
		let prevTime = -Infinity;
		for (const clip of track.clips) {
			if (clip.time < currentTime) {
				prevTime = Math.max(prevTime, clip.time);
			}
			if (clip.time + clip.duration < currentTime) {
				prevTime = Math.max(prevTime, clip.time + clip.duration);
			}
		}
		
		return prevTime;
	}

	let prevTime = -Infinity;
	prevTime = Math.max(prevTime, findPrevInAudioTrack(timeline.musicTrack));
	prevTime = Math.max(prevTime, findPrevInAudioTrack(timeline.ambienceTrack));
	prevTime = Math.max(prevTime, findPrevInAudioTrack(timeline.foleyTrack));

	for (const dancer of timeline.dancers) {
		prevTime = Math.max(prevTime, findPrevInAudioTrack(dancer.aboveHeadTrack));
		prevTime = Math.max(prevTime, findPrevInAudioTrack(dancer.headTrack));
		prevTime = Math.max(prevTime, findPrevInAudioTrack(dancer.torsoTrack));
		prevTime = Math.max(prevTime, findPrevInAudioTrack(dancer.legsTrack));
		prevTime = Math.max(prevTime, findPrevInAudioTrack(dancer.groundTrack));
	}

	if (prevTime > -Infinity) {
		return prevTime;
	}

	return null;
}

export const getAudioTrack = (timeline: ITimeline, trackId: ITrackId) => {
	switch (trackId.trackType) {
		case TrackType.Global: {
			switch (trackId.type) {
				case GlobalTrackType.Music: return timeline.musicTrack;
				case GlobalTrackType.Ambience: return timeline.ambienceTrack;
				case GlobalTrackType.Foley: return timeline.foleyTrack;
			}
		}
		case TrackType.Dancer: {
			const dancer = timeline.dancers.find(dancer => dancer.id === trackId.dancerId);
			if (dancer === undefined) return null;
			switch (trackId.type) {
				case DancerTrackType.AboveHead: return dancer.aboveHeadTrack;
				case DancerTrackType.Head: return dancer.headTrack;
				case DancerTrackType.Torso: return dancer.torsoTrack;
				case DancerTrackType.Legs: return dancer.legsTrack;
				case DancerTrackType.Ground: return dancer.groundTrack;
			}
		}
	}

	return null;
}

// Function to build a track name string from the track in timeline
export const getTrackName = (timeline: ITimeline, trackId: ITrackId) => {
	if(trackId !== null) {
		switch (trackId.trackType) {
			case TrackType.Global: {
				switch (trackId.type) {
					case GlobalTrackType.Music: return "Global Music";
					case GlobalTrackType.Ambience: return "Global Ambience";
					case GlobalTrackType.Foley: return "Global Foley";
				}
			}
			case TrackType.Dancer: {
				const dancer = timeline.dancers.find(dancer => dancer.id === trackId.dancerId);
				if (dancer === undefined) return null;
				switch (trackId.type) {
					case DancerTrackType.AboveHead: return "Dancer "+ dancer.id + " Above Head";
					case DancerTrackType.Head: return "Dancer "+ dancer.id + " Head";
					case DancerTrackType.Torso: return "Dancer "+ dancer.id + " Torso";
					case DancerTrackType.Legs: return "Dancer "+ dancer.id + " Legs";
					case DancerTrackType.Ground: return "Dancer "+ dancer.id + " Ground";
				}
			}
		}
	}

	return null;
}

// Function to get all the audio tracks in the given timeline
export const getAllAudioTracks = (timeline: ITimeline) => {
	let audioTracks: IAudioTrack[] = [timeline.musicTrack, timeline.ambienceTrack, timeline.foleyTrack];
	for (const dancer of timeline.dancers) {
		audioTracks.push(dancer.aboveHeadTrack, dancer.headTrack, dancer.torsoTrack, dancer.legsTrack, dancer.groundTrack);
	}
	return audioTracks;
}

// Function to get all the gaps in a given track
export const getGapsInTrack = (timeline: ITimeline, audioTrack: IAudioTrack) => {
	if (audioTrack === null) return;
	
	let audioGaps: IAudioGapId[] = [];
	
	let prevTime = 0;
	for (const clip of [...audioTrack?.clips].sort((a, b) => a.time - b.time)) {
		if (clip.time > prevTime) {
			audioGaps.push({
				afterClipId: clip.id
			});
		}

		prevTime = clip.time+clip.duration;
	}

	if(prevTime == 0)
		return null;

	return audioGaps;
}

// Function to get the element (audio or gap) in a given track, at the given time. If time is null, it returns the first possible element in the timeline
export const getAudioOrGapInTrackAtTime = (timeline: ITimeline, audioTrack: IAudioTrack, atTime: number | null) => {
	const audioGaps = getGapsInTrack(timeline, audioTrack);
	let resultElement = null;
	let sortedTrack = [...audioTrack.clips].sort((a, b) => a.time - b.time);

	// Get first element in the audioTrack track, considering audioGaps gaps
	if(atTime == null) {
		let firstAudioClip = sortedTrack?.[0];
		let firstAudioGap = audioGaps?.[0]; 
		let firstAudioGapStats = getGapStats(timeline, firstAudioGap!);
		let firstAudioGapTime = 0;
		if(firstAudioGapStats != null) {
			firstAudioGapTime = firstAudioGapStats.time;
		}

		// Check if gap or clip comes first
		if(firstAudioGap == null || firstAudioClip.time < firstAudioGapTime) {
			// It's a clip
			resultElement = firstAudioClip;
		} else {
			// It's a gap
			resultElement = firstAudioGap;
		}
	}
	// Get element at atTime position in audioTrack track, considering audioGaps gaps
	else {
		// Get audio containing atTime time...
		let audioClip = sortedTrack?.find((el) => el.time <= atTime! && el.time+el.duration >= atTime!);
		if(audioClip != null) {
			resultElement = audioClip;
		} else {
			// ... otherwise, get the corresponding gap
			let audioClipAfter = sortedTrack?.find((el) => el.time > atTime!);
			if(audioClipAfter != null) {
				let audioGap = audioGaps!.find((el) => el.afterClipId = audioClipAfter!.id);
				if(audioGap != null) {
					// It's a gap
					resultElement = audioGap;
				} 
				else {
					// It's a clip
					resultElement = audioClipAfter;
				}
			}
		}
	}

	// If didn't find, get the last clip
	if(resultElement == null) {
		resultElement = sortedTrack[sortedTrack.length-1];
	}

	return resultElement;
}

// Function to retrieve an audio track given a specific gap
export const getAudioTrackWithGap = (timeline:ITimeline, audioGapId: IAudioGapId) => {
	const audioTracks = getAllAudioTracks(timeline);
	let audioTrack = null;
	if(audioGapId != null) {
		for (const track of audioTracks) {
			let audioClip = track.clips!.find((el) => el.id == audioGapId.afterClipId);
			if(audioClip != null) {
				audioTrack = getAudioTrack(timeline, audioClip.trackId!);
				break;
			}	
		}
	}
	return audioTrack;
}

// Function used to retrieve stats from a Gap
export const getGapStats = (timeline:ITimeline, audioGapId: IAudioGapId) => {
	let time = 0;
	let duration = 0;
	let prevClip = null;
	let trackId = null;
	const audioTrack = getAudioTrackWithGap(timeline, audioGapId);
	if(audioTrack != null) {
		for (const clip of [...audioTrack!.clips].sort((a, b) => a.time - b.time)) {
			if (clip.id == audioGapId.afterClipId) {
				if(prevClip == null) {
					time = 0;
					duration = clip.time;
				} else {
					time = (prevClip.time + prevClip.duration);
					duration = clip.time - time;
				}
				trackId = clip.trackId!;
				break;
			}
			prevClip = clip;
		}
	}
	return {time: time, duration: duration, trackId: trackId};
}

// Function to treat the clip name and return a string value
export const getClipName = (audioFiles: IAudioFiles, audioFileId: number) => {
	let clipName = audioFiles.files.find(
		(file) => file.id === audioFileId
	)?.name;
	if(clipName == "" || clipName == undefined) { clipName = audioFileId.toString(); }
	return clipName;
}